import {jwtDecode} from 'jwt-decode';
import Logger from './logger';
import authStorage from './authStorage';

// Utiliser 'token' comme clé principale pour la compatibilité avec ChannelConnections
const TOKEN_KEY = 'token';
const USER_KEY = 'auth_user';

const JwtUtils = {
  /**
   * Store JWT token in localStorage
   * @param {string} token - JWT token
   */
  storeToken: (token) => {
    // Stocker avec les deux clés pour assurer la compatibilité
    authStorage.setItem(TOKEN_KEY, token);
    authStorage.setItem('auth_token', token); // Clé alternative pour compatibilité
    console.log('Token stored successfully');
  },

  /**
   * Get stored JWT token from localStorage
   * @returns {string|null} - JWT token or null if not found
   */
  getStoredToken: () => {
    // Essayer d'abord avec 'token', puis 'auth_token'
    let token = authStorage.getItem(TOKEN_KEY);
    if (!token) {
      token = authStorage.getItem('auth_token');
    }
    
    if (token && token !== 'null' && token !== 'undefined') {
      return token;
    }
    
    console.log('No valid token found in localStorage');
    return null;
  },

  /**
   * Remove JWT token from localStorage
   */
  removeToken: () => {
    authStorage.removeItem(TOKEN_KEY);
    authStorage.removeItem('auth_token');
    console.log('Token removed from localStorage');
  },

  /**
   * Store user information in localStorage
   * @param {Object} user - User object
   */
  storeUser: (user) => {
    authStorage.setItem(USER_KEY, JSON.stringify(user));
    // Aussi stocker avec 'user' pour compatibilité
    authStorage.setItem('user', JSON.stringify(user));
  },

  /**
   * Get stored user information from localStorage
   * @returns {Object|null} - User object or null if not found
   */
  getStoredUser: () => {
    let userStr = authStorage.getItem(USER_KEY);
    if (!userStr) {
      userStr = authStorage.getItem('user');
    }
    
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      Logger.error('Error parsing user from localStorage', error);
      return null;
    }
  },

  /**
   * Remove user information from localStorage
   */
  removeUser: () => {
    authStorage.removeItem(USER_KEY);
    authStorage.removeItem('user');
  },

  /**
   * Check if user is authenticated (has valid token)
   * @returns {boolean} - True if authenticated, false otherwise
   */
  isAuthenticated: () => {
    const token = JwtUtils.getStoredToken();
    const isValid = !!token && !JwtUtils.isTokenExpired(token);
    console.log('Authentication check:', { hasToken: !!token, isValid });
    return isValid;
  },

  /**
   * Check if JWT token is expired
   * @param {string} token - JWT token
   * @returns {boolean} - True if token is expired, false otherwise
   */
  isTokenExpired: (token) => {
    if (!token || token === 'null' || token === 'undefined') return true;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Ajouter une marge de 30 secondes pour éviter les problèmes de timing
      return decoded.exp < (currentTime + 30);
    } catch (error) {
      Logger.error('Error decoding token', error);
      return true; // Assume token is expired if it can't be decoded
    }
  },
  
  /**
   * Get decoded JWT token payload
   * @param {string} token - JWT token
   * @returns {Object|null} - Decoded token payload or null if decoding fails
   */
  decodeToken: (token) => {
    if (!token || token === 'null' || token === 'undefined') return null;
    
    try {
      return jwtDecode(token);
    } catch (error) {
      Logger.error('Error decoding token', error);
      return null;
    }
  },

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean} - True if user has role, false otherwise
   */
  hasRole: (role) => {
    const token = JwtUtils.getStoredToken();
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.roles && decoded.roles.includes(role);
    } catch (error) {
      Logger.error('Error checking user role', error);
      return false;
    }
  },

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} - True if user has permission, false otherwise
   */
  hasPermission: (permission) => {
    const token = JwtUtils.getStoredToken();
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.permissions && decoded.permissions.includes(permission);
    } catch (error) {
      Logger.error('Error checking user permission', error);
      return false;
    }
  },

  /**
   * Validate token and clean up if invalid
   * @returns {boolean} - True if token is valid, false otherwise
   */
  validateAndCleanToken: () => {
    const token = JwtUtils.getStoredToken();
    if (!token || JwtUtils.isTokenExpired(token)) {
      console.log('Invalid or expired token detected, cleaning up');
      JwtUtils.removeToken();
      JwtUtils.removeUser();
      return false;
    }
    return true;
  }
};

export default JwtUtils;

